// A copy das viagens de ônibus da conversa no vocabulário do intelligence-ui-kit
// — as mesmas palavras da bancada (`busBooking.results.*` do web-chat), no
// catálogo do genui-kit (`busResults.*`). O kit desenha; aqui só se traduz.
import {
  busDurationLabel,
  busTimeLabel,
  minuteLabel,
  type BusFilterableOffer,
  type BusLegStepView,
  type BusOfferShortlistCopy,
  type BusResultsFiltersCopy,
  type BusResultsPageCopy,
  type BusResultsViewCopy,
} from '@voll-intelligence/intelligence-ui-kit';
import { formatWeekdayDayMonth } from '@voll-intelligence/dates';
import { fmtMoney } from '../../shared/ui/flight_format.js';
import type { useGenUiI18n } from '../../shared/ui/i18n.js';
import type { ChatUiBusOffer } from '@voll-intelligence/genui-contract';

type I18n = ReturnType<typeof useGenUiI18n>;
type T = I18n['t'];

export type BusLeg = 'outbound' | 'return';

export function busLegName(t: T, leg: BusLeg): string {
  return leg === 'outbound' ? t('busResults.legOutbound') : t('busResults.legReturn');
}

/** "Ter, 25 ago" — o dia da partida, no locale da conversa. */
export function busWeekdayDay(date: Date, locale: I18n['locale']): string {
  return formatWeekdayDayMonth(date, locale, new Date());
}

export function busViewCopy({ t, locale }: I18n, leg: BusLeg): BusResultsViewCopy {
  const name = busLegName(t, leg);
  return {
    headline: (departure) =>
      t('busResults.headline', { leg: name, date: busWeekdayDay(departure, locale) }),
    direct: t('busResults.card.direct'),
    withConnection: t('busResults.card.withConnection'),
    withConnectionAt: (stop) => t('busResults.card.withConnectionAt', { stop }),
    // A régua do frame da conversa: "1 conexão" na cabeça; a parada vai no
    // `Detalhes` (`connectionStop`).
    connections: (count) =>
      t(count === 1 ? 'busOffers.connection_one' : 'busOffers.connection_other', { count }),
    connectionStop: (stop) => t('busResults.card.connectionStop', { stop }),
    connectionTimes: (arrival, departure) =>
      t('busResults.card.connectionTimes', { arrival, departure }),
    connectionWait: (duration) => t('busResults.card.connectionWait', { duration }),
    seatsLeft: (seatClass, count) =>
      t(count === 1 ? 'busResults.card.seatsLeft_one' : 'busResults.card.seatsLeft_other', {
        seatClass,
        count,
      }),
    outOfPolicy: t('busResults.outOfPolicy'),
    priceLabel: (amount, currency) => fmtMoney({ amount, currency }),
    preferredCarrier: t('busResults.card.preferredCarrier'),
    preferredSeatClass: t('busResults.card.preferredSeatClass'),
    preferredDeparture: t('busResults.card.preferredDeparture'),
  };
}

function cardCopy(t: T): BusOfferShortlistCopy['card'] {
  return {
    selectLabel: t('busResults.card.select'),
    perPersonLabel: t('busResults.card.perPerson'),
    detailsLabel: t('busResults.card.details'),
  };
}

function legsCopy(t: T): BusOfferShortlistCopy['legs'] {
  return {
    label: t('busResults.legs.label'),
    doneStepLabel: (step) =>
      step === 1 ? t('busResults.legs.outboundDone') : t('busResults.legs.returnDone'),
  };
}

export function busShortlistCopy(t: T): BusOfferShortlistCopy {
  return {
    resultsLabel: t('busResults.shortlist'),
    card: cardCopy(t),
    legs: legsCopy(t),
    seeAllLabel: (total) => t('busResults.seeAll', { count: total }),
    seatsTitle: t('busResults.seatsTitle'),
    seatsCloseLabel: t('busResults.seatsClose'),
  };
}

export function busPageCopy(t: T, title: string, chosen: number, legs: number): BusResultsPageCopy {
  return {
    title,
    chosenLabel: t(legs === 1 ? 'busResults.chosen_one' : 'busResults.chosen_other', {
      chosen,
      count: legs,
    }),
    shareLabel: t('busResults.share'),
    totalLabel: (total) =>
      t(total === 1 ? 'busResults.total_one' : 'busResults.total_other', { count: total }),
    visibleLabel: (visible, total) => t('busResults.visible', { visible, total }),
    resultsLabel: t('busResults.resultsLabel'),
    applyFiltersLabel: t('busResults.applyFilters'),
    loadMoreLabel: t('busResults.loadMore'),
    datesLabel: t('busResults.dates'),
    filteredEmpty: {
      title: t('busResults.filteredEmpty'),
      clearLabel: t('busResults.clearFilters'),
    },
    sort: {
      sortLabel: t('busResults.sort'),
      orderLabel: (order) => t(`busResults.order.${order}`),
    },
    chips: {
      toolbarLabel: t('busResults.chips.toolbar'),
      backLabel: t('busResults.chips.back'),
      policyLabel: t('busResults.chips.policy'),
    },
    card: cardCopy(t),
    legs: legsCopy(t),
    seatsTitle: t('busResults.seatsTitle'),
    seatsCloseLabel: t('busResults.seatsClose'),
  };
}

export function busFiltersCopy(t: T, currency: string): BusResultsFiltersCopy {
  return {
    filtersLabel: t('busResults.filters.filters'),
    collapseFiltersLabel: t('busResults.filters.collapse'),
    closeFiltersLabel: t('busResults.filters.close'),
    resetFiltersLabel: t('busResults.filters.reset'),
    policyLabel: t('busResults.filters.policy'),
    withinPolicyLabel: t('busResults.filters.withinPolicy'),
    seatClassLabel: t('busResults.filters.seatClass'),
    fromPriceLabel: (amount) => fmtMoney({ amount, currency }),
    timeLabel: t('busResults.filters.time'),
    departureLabel: t('busResults.filters.departure'),
    arrivalLabel: t('busResults.filters.arrival'),
    timeRangeLabel: ({ from, to }) => `${minuteLabel(from)} - ${minuteLabel(to)}`,
    priceLabel: t('busResults.filters.price'),
    minimumPriceLabel: t('busResults.filters.minimumPrice'),
    maximumPriceLabel: t('busResults.filters.maximumPrice'),
    companyLabel: t('busResults.filters.company'),
  };
}

/** A ida já fechada, no card da volta. A logo, a classe e as poltronas vêm só
 *  nos blocos novos (frame 06). */
export interface BusOutboundPickView {
  departure: { dateTime: string };
  arrival: { dateTime: string };
  carrierName: string;
  logoUrl?: string;
  seatClass?: string;
  seats?: readonly string[];
}

/** "19:45 → 06:30 · Convencional · poltronas 12 e 28" — a ida fechada no passo
 *  concluído. Com a logo, a viação sai do texto (a logo a nomeia). */
function chosenTripDetail(i18n: Pick<I18n, 't' | 'locale'>, pick: BusOutboundPickView): string {
  const { t, locale } = i18n;
  const seats = pick.seats ?? [];
  const parts = [
    `${busTimeLabel(pick.departure.dateTime)} → ${busTimeLabel(pick.arrival.dateTime)}`,
    pick.logoUrl ? null : pick.carrierName,
    pick.seatClass ?? null,
    seats.length > 0
      ? t(seats.length === 1 ? 'busResults.legs.seat_one' : 'busResults.legs.seat_other', {
          seats: new Intl.ListFormat(locale, { type: 'conjunction' }).format(seats),
        })
      : null,
  ];
  return parts.filter(Boolean).join(' · ');
}

/**
 * Os passos do stepper do card: só na ida e volta. No card da IDA, a ida em
 * cena e a volta à espera; no da VOLTA (cuja rota já vem invertida), a ida
 * concluída com a viagem escolhida e a volta em cena. Só ida: nenhum passo.
 */
export function busLegSteps(
  i18n: Pick<I18n, 't' | 'locale'>,
  card: {
    roundTrip: boolean;
    leg: BusLeg;
    cities: { departure: string; arrival: string };
    outboundPick?: BusOutboundPickView;
  },
): BusLegStepView[] {
  const { t } = i18n;
  if (!card.roundTrip) return [];
  const { departure, arrival } = card.cities;
  if (card.leg === 'outbound') {
    return [
      {
        route: `${departure} → ${arrival}`,
        status: 'current',
        detail: t('busResults.legs.choosing'),
      },
      {
        route: `${arrival} → ${departure}`,
        status: 'pending',
        detail: t('busResults.legs.pending'),
      },
    ];
  }
  const pick = card.outboundPick;
  return [
    {
      route: `${arrival} → ${departure}`,
      status: 'done',
      detail: pick ? chosenTripDetail(i18n, pick) : t('busResults.legs.outboundDone'),
      ...(pick?.logoUrl ? { logo: { src: pick.logoUrl, alt: pick.carrierName } } : {}),
    },
    {
      route: `${departure} → ${arrival}`,
      status: 'current',
      detail: t('busResults.legs.choosing'),
    },
  ];
}

/** O porquê de uma viagem estar na seleção curta (curadoria do agente). */
type BusHighlight = NonNullable<ChatUiBusOffer['highlight']>;

const HIGHLIGHT_ICON = {
  recommended: 'recommended',
  cheapest: 'cheapest',
  fastest: 'closest',
} as const;

/** A nota de mérito no pé do card, a partir dos FATOS do agente. */
function meritNoteOf(t: T, offer: ChatUiBusOffer, highlight: BusHighlight): string {
  if (highlight.kind === 'recommended') {
    return t('busResults.merit.recommended', { seatClass: offer.seatClass });
  }
  if (highlight.kind === 'cheapest') {
    const amount = fmtMoney({ amount: highlight.cheaperBy, currency: offer.price.currency });
    const stop = offer.connections > 0 ? offer.connection?.stopName : undefined;
    return stop
      ? t('busResults.merit.cheapestAt', { amount, stop })
      : t('busResults.merit.cheapest', { amount });
  }
  const duration = busDurationLabel(highlight.fasterByMinutes);
  return offer.connections === 0
    ? t('busResults.merit.fastestDirect', { duration })
    : t('busResults.merit.fastest', { duration });
}

/**
 * As views da conversa com o selo e a nota de cada viagem da seleção curta: o
 * selo do eixo (Recomendado sólido, Menor preço, Menos tempo) vem antes dos
 * selos pessoais das Preferências, e a nota diz o porquê.
 */
export function withHighlights(
  t: T,
  views: readonly BusFilterableOffer[],
  offers: readonly ChatUiBusOffer[],
): BusFilterableOffer[] {
  const byId = new Map(offers.map((offer) => [offer.offerId, offer]));
  return views.map((facts) => {
    const offer = byId.get(facts.offer.id);
    const highlight = offer?.highlight;
    if (!offer || !highlight) return facts;
    const badge = {
      id: `highlight-${highlight.kind}`,
      label: t(`busResults.badge.${highlight.kind}`),
      tone: highlight.kind === 'recommended' ? ('within' as const) : ('unknown' as const),
      icon: HIGHLIGHT_ICON[highlight.kind],
    };
    return {
      ...facts,
      offer: {
        ...facts.offer,
        badges: [badge, ...facts.offer.badges],
        meritNote: meritNoteOf(t, offer, highlight),
      },
    };
  });
}
